import { addressSearchProps } from '@/core/utils/address-search';
import { useProvinceWards, wardLabel } from '@/app/home/components/address/useProvinceWards';
import { Alert, ConfigProvider, DatePicker, Form, Input, message, Modal, Select } from "antd";
import dayjs, { Dayjs } from 'dayjs';
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { TollAreaModel } from "@/core/models/toll-area/toll-area.model";
import container from "@/lib/di/container";
import { TollAreaRepo } from "@/core/repos/tables/toll-area.repo";
import React from "react";
import { ReadMeterPeriodModel } from "@/core/models/read-meter-period.model";
import { ReadMeterPeriodRepo } from "@/core/repos/tables/read-meter-period.repo";
import { dayjsPipe } from "@/core/pipes/dayjs.pipe";
import { AddressRepo } from "@/core/repos/address.repo";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import { usePermissionStore } from "@/core/stores/permission.store";
import { SaveButton } from "@/app/home/components/table/SaveButton";
import { useDrafts } from "@/core/stores/drafts.store";
import { ResetDraftButton } from '@/app/home/components/drafts/ResetDraftButton';
import { DEF_DATE_FORMAT } from "@/app/home/utils/global.utils";


const tollAreaRepo = container.get(TollAreaRepo);
const readMeterPeriodRepo = container.get(ReadMeterPeriodRepo);
const addressRepo = container.get(AddressRepo);

interface DetailModalProps {
    isModalOpen: boolean,
    setIsModalOpen: Dispatch<SetStateAction<boolean>>,
    data: ReadMeterPeriodModel,
}

const INPUT_WIDTH = 600;

type ReadingPeriodForm = ReadMeterPeriodModel & { readingPeriod: Dayjs | null };

function getReadingPeriod(values: Partial<ReadMeterPeriodModel>) {
    const year = Number(values.year);
    const month = Number(values.month);
    return Number.isInteger(year) && year >= 1 && year <= 9999 && Number.isInteger(month) && month >= 1 && month <= 12
        ? dayjs().startOf('year').year(year).month(month - 1).startOf('month')
        : dayjs().startOf('month');
}

export const DetailModal: React.FC<DetailModalProps> = ({isModalOpen, setIsModalOpen, data}) => {
    const permissionStore = usePermissionStore();
    const [form] = Form.useForm<ReadingPeriodForm>();
    const { provinces, wards, loadAddress, handleProvinceChange, handleWardChange } = useProvinceWards(form, addressRepo);
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [, setTollAreas] = useState<TollAreaModel[]>([]);
    const [messageApi, contextHolder] = message.useMessage();    

    // Draft logic for ReadMeterPeriodModel
    const draft = useDrafts((state) => state.getDraft('ReadMeterPeriodModel'));
    const _saveDraft = useDrafts((state) => state.saveDraft);
    const saveDraft = () => {
        const { readingPeriod, ...values } = form.getFieldsValue();
        _saveDraft('ReadMeterPeriodModel', {
            ...values,
            year: readingPeriod?.year(),
            month: readingPeriod ? readingPeriod.month() + 1 : undefined,
        });
    };
    const _clearDraft = useDrafts((state) => state.clearDraft);
    const clearDraft = () => {
        _clearDraft('ReadMeterPeriodModel');
        form.setFieldsValue({ ...data, readingPeriod: getReadingPeriod(data) });
        setSaveError(null);
        void loadAddress(data);
    };

    // Attach hooks to handle data changes
    const handleDataChange = async () => {
        const values = !data.id && draft ? { ...data, ...draft } : data;
        form.setFieldsValue({ ...values, readingPeriod: getReadingPeriod(values) });
        setSaveError(null);
        setIsEdit(!!data.id);
        await loadAddress(values);
    };
    useEffect(() => {
        if (isModalOpen) void handleDataChange();
    }, [data, isModalOpen])

    // Initialize select options data
    const populateTollAreas = async () => {
        const _tollAreas = await tollAreaRepo.getAll();
        setTollAreas(_tollAreas);
    }
    const init = async () => {
        await populateTollAreas();
    }
    // componentDidMount equivalent, run exclusively on client-side
    useEffect(() => {init()}, []);

    const handleSave = async () => {
        try {
            setLoading(true);
            setSaveError(null);
            const { readingPeriod, ...formData } = await form.validateFields();
            const result = await readMeterPeriodRepo.save({
                ...data,
                ...formData,
                ...(isEdit ? { id: data.id } : { year: readingPeriod!.year(), month: readingPeriod!.month() + 1 }),
            });
            if (result?.code !== '00') {
                setSaveError(result?.errorMessage || 'Không thể lưu kỳ đọc chỉ số. Vui lòng thử lại.');
                return;
            }
            if (!isEdit) clearDraft();
            setIsModalOpen(false);
            const summary = !isEdit ? result.result : undefined;
            const skippedWarning = summary && summary.skippedMissingMeterCount > 0
                ? (summary.warningMessage || result.errorMessage || `Đã tạo lịch và chỉ số cho kỳ đọc. Bỏ qua ${summary.skippedMissingMeterCount} khách hàng thiếu mã đồng hồ: ${(summary.missingMeterCustomerCodes || []).join(', ')}.`)
                : (!isEdit && result.errorMessage && result.errorMessage.includes('thiếu mã đồng hồ') ? result.errorMessage : null);
            if (skippedWarning) {
                messageApi.warning({
                    content: skippedWarning,
                    duration: 10,
                });
            } else {
                messageApi.success({ content: result.errorMessage || (isEdit ? 'Đã cập nhật kỳ đọc chỉ số' : 'Đã tạo lịch và chỉ số cho kỳ đọc') });
            }
        } catch (error) {
            if (!(error && typeof error === 'object' && 'errorFields' in error)) {
                setSaveError('Không thể lưu kỳ đọc chỉ số. Vui lòng thử lại.');
            }
        } finally {
            setLoading(false);
        }
    };
    const handleCancel = () => {
      if (!isEdit) saveDraft();
      setIsModalOpen(false);
    };

    return (
        <ConfigProvider theme={MODAL_THEME}>
            {contextHolder}
           <Modal open={isModalOpen} onCancel={handleCancel} width={670} centered
                styles={{
                    mask: {
                        backdropFilter: 'blur(2px)',
                        backgroundColor: 'var(--primary-mask)'
                    }
                }}
                footer={[
                    <div key={1} className="w-full flex flex-row justify-end px-1.5">
                        <SaveButton isEdit={isEdit} loading={loading} onClick={handleSave}/>
                    </div>,
                ]}
            >
                <div className="flex flex-row w-full items-center justify-start select-none">
                    <p className="ml-2 text-2xl font-bold text-primary">CHI TIẾT KỲ ĐỌC CHỈ SỐ</p>
                    {!isEdit && <ResetDraftButton onClick={clearDraft} />}
                </div>
                <div className="flex flex-row justify-center mt-6">
                    <Form<ReadingPeriodForm>
                        name="basic"
                        className="flex flex-col items-end"
                        form={form}
                        layout="vertical"
                        disabled={isEdit&&!permissionStore.page.edit}
                    >
                        {
                            isEdit
                            ?<Form.Item<ReadMeterPeriodModel>
                                label="Khu vực thu phí"
                                name="tollArea"
                                getValueProps={(e) => ({value: e.name})}
                            >
                                <Input type="text" style={{width: INPUT_WIDTH}} disabled/>
                            </Form.Item>
                            :(
                                <div>
                                    <Form.Item<ReadingPeriodForm>
                                        label="Kỳ đọc"
                                        name="readingPeriod"
                                        rules={[{ required: true, message: 'Vui lòng chọn tháng/năm!' }]}
                                    >
                                        <DatePicker picker="month" format="MM/YYYY" placeholder="Chọn tháng/năm" style={{width: INPUT_WIDTH}} />
                                    </Form.Item>
                                    <Form.Item<ReadMeterPeriodModel>
                                        label="Tỉnh/ TP"
                                        name="provinceId"
                                        rules={[{ required: true, message: 'Bạn phải chọn Tỉnh/ TP!' }]}
                                    >
                                        <Select {...addressSearchProps} placeholder="Chọn" style={{width: INPUT_WIDTH}} onChange={handleProvinceChange}>
                                            {provinces.map(province => <Select.Option key={province.id} value={province.id}>{province.name}</Select.Option>)}
                                        </Select>
                                    </Form.Item>
                            <Form.Item<ReadMeterPeriodModel> name="districtId" hidden><Input /></Form.Item>
                                    <Form.Item<ReadMeterPeriodModel>
                                        label="Phường/ Xã"
                                        name="wardId"
                                    >
                                        <Select {...addressSearchProps} placeholder="Chọn" style={{width: INPUT_WIDTH}} onChange={handleWardChange}>
                                            {wards.map(ward => <Select.Option key={ward.id} value={ward.id}>{wardLabel(ward)}</Select.Option>)}
                                        </Select>
                                    </Form.Item>
                                </div>
                            )
                        }
                        
                        <Form.Item<ReadMeterPeriodModel>
                            label="Từ ngày"
                            name="fromDate"
                            getValueProps={(i) => ({value: dayjsPipe(i)})}
                        >
                            <DatePicker format={DEF_DATE_FORMAT}placeholder="Chọn ngày bắt đầu" style={{width: INPUT_WIDTH}}></DatePicker>
                        </Form.Item>
                        <Form.Item<ReadMeterPeriodModel>
                            label="Đến ngày"
                            name="toDate"
                            getValueProps={(i) => ({value: dayjsPipe(i)})}
                        >
                            <DatePicker format={DEF_DATE_FORMAT}placeholder="Chọn ngày kết thúc" style={{width: INPUT_WIDTH}}></DatePicker>
                        </Form.Item>
                        
                        <Form.Item<ReadMeterPeriodModel>
                            label="Cho phép đọc"
                            name="isAllowReading"
                        >
                            <Select placeholder="Chọn trạng thái cho phép đọc" style={{width: INPUT_WIDTH}}>
                                <Select.Option value={true}>Có</Select.Option>
                                <Select.Option value={false}>Không</Select.Option>
                            </Select>
                        </Form.Item>
                    </Form>
                </div>
                {saveError && <Alert className="mb-5 mx-2" type="error" showIcon message="Chưa lưu được kỳ đọc chỉ số"
                    description={<span style={{ whiteSpace: 'pre-line' }}>{saveError}</span>} />}
                <div className="mb-5 mx-2 border-t border-dashed border-gray-300"></div>
            </Modal>
        </ConfigProvider>
    )
}
